import { default as React } from 'react';
import { useEffect, useState, useRef } from 'react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { DocsHelpIcon } from './components';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { makeStyles } from '@mui/styles';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  templateItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(1, 0),
  },
  templateName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  templateDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  languageChip: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: '0.75rem',
  },
}));

interface Template {
  id?: number;
  fullName: string;
  name: string;
  nameWithDisplayOwner?: string;
  description: string;
  language?: string;
  ownerDisplayLogin?: string;
  avatarUrl?: string;
  htmlUrl?: string;
  isTemplate?: boolean;
}

/**
 * Custom field component to fetch and display GitHub template repositories
 */
const GithubTemplatePickerComponent = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    uiSchema,
    idSchema,
    formContext,
  } = props;

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string>('');

  // Track last fetched org to avoid refetching when switching template source
  const lastFetchedOrgRef = useRef<string>('');
  const cachedTemplatesRef = useRef<Template[]>([]);

  const classes = useStyles();
  const config = useApi(configApiRef);

  // Get templateSource and selected organization from form context
  const allFormData = formContext?.formData || {};
  const templateSource = allFormData.templateSource || '';

  // Get selected org from form context (format: "github.com/orgname")
  const selectedOrgRaw = allFormData.host?.githubOrg || '';
  const orgName = selectedOrgRaw.split('/').pop() || '';

  // Fetch templates when templateSource is 'github'
  useEffect(() => {
    // Only fetch when this field should be visible
    // This guard MUST be before sessionStorage restore to prevent infinite loops
    // when the clear effect sets formData to undefined
    if (templateSource !== 'github') {
      return;
    }

    // Restore from sessionStorage when returning to form
    if (!formData) {
      const savedFormState = sessionStorage.getItem('github-repo-form-state');

      if (savedFormState) {
        try {
          const parsedState = JSON.parse(savedFormState);
          const ONE_HOUR = 60 * 60 * 1000;
          const isStale =
            parsedState.timestamp &&
            Date.now() - parsedState.timestamp > ONE_HOUR;

          if (!isStale && parsedState.template) {
            onChange(parsedState.template);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to restore template field:', err);
        }
      }
    }

    // If no org selected, stop loading and wait for user to select one
    if (!orgName) {
      setLoading(false);
      setTemplates([]);
      return;
    }

    // If we already fetched templates for this org, use cached data
    if (
      lastFetchedOrgRef.current === orgName &&
      cachedTemplatesRef.current.length > 0
    ) {
      setTemplates(cachedTemplatesRef.current);
      setLoading(false);
      return;
    }

    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setFetchError('');

        // Clear previous selection when fetching new templates (org changed)
        onChange('');

        // Call backend API to fetch templates for the selected organization
        const backendUrl = config.getString('backend.baseUrl');

        // Get user's GitHub token from the form (from GithubSsoLogin field)
        // The form data structure is: { host: { token: "...", ... }, ... }
        const githubToken = formContext?.formData?.host?.token;

        // Use Authorization header (secure) instead of query parameter
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (githubToken) {
          headers.Authorization = `Bearer ${githubToken}`;
        }

        // Pass the selected organization as a query parameter
        const url = new URL(`${backendUrl}/api/github-repo-api/templates`);
        url.searchParams.set('org', orgName);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch templates: ${response.status} - ${response.statusText}`,
          );
        }

        const data = await response.json();

        // Templates to hide (available as standard templates instead)
        const hiddenTemplates = [
          'mediwareinc/terraform-module-template',
          'mediwareinc/terraform-infra-gcp-template',
        ];

        // Filter out hidden templates and add "None" option at the beginning
        const filteredTemplates = data.templates.filter(
          (t: any) => !hiddenTemplates.includes(t.fullName),
        );

        const templatesWithNone: Template[] = [
          {
            fullName: '',
            name: 'None (Empty Repository)',
            description: 'Start fresh with an empty repository',
          },
          ...filteredTemplates.map((t: any) => ({
            id: t.id,
            fullName: t.fullName,
            name: t.name,
            nameWithDisplayOwner: t.nameWithDisplayOwner || t.fullName,
            description: t.description || 'No description available',
            language: t.language,
            ownerDisplayLogin: t.ownerDisplayLogin,
            avatarUrl: t.avatarUrl,
            htmlUrl: t.htmlUrl,
            isTemplate: t.isTemplate,
          })),
        ];

        // Cache templates for this org
        lastFetchedOrgRef.current = orgName;
        cachedTemplatesRef.current = templatesWithNone;
        setTemplates(templatesWithNone);
        setLoading(false);
      } catch (err: any) {
        // The previous behaviour fell back to hard-coded template names
        // (mediwareinc/template-nodejs, etc.) that do not exist as real repos —
        // selecting one always produced a confusing 404 at repository-creation
        // time. Instead, surface the real error and offer ONLY the empty-repository
        // option, which always works. This makes a token/sign-in problem visible
        // rather than masking it behind dead templates.
        // eslint-disable-next-line no-console
        console.error('Failed to fetch GitHub templates:', err);
        setFetchError(
          'Could not load templates from GitHub (check your sign-in / token). ' +
            'You can still create an empty repository below.',
        );
        setTemplates([
          {
            fullName: '',
            name: 'None (Empty Repository)',
            description: 'Start fresh with an empty repository',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [
    config,
    formData,
    formContext?.formData?.host?.token,
    templateSource,
    orgName,
    onChange,
  ]); // Re-fetch when user logs in or org changes (cached for template source switches)

  // Clear value when templateSource switches away from 'github'
  // This prevents stale template values from persisting in form data
  // Note: Using undefined (not '') to fully clear the field
  useEffect(() => {
    if (templateSource !== 'github' && formData) {
      // Cast to unknown first since Backstage's onChange type doesn't permit undefined,
      // but passing undefined is the correct way to clear the field value
      onChange(undefined as unknown as string);
    }
  }, [templateSource, formData, onChange]);

  // Only show when templateSource is 'github'
  if (templateSource !== 'github') {
    return null;
  }

  const handleChange = (event: any) => {
    onChange(event.target.value as string);
  };

  const id = idSchema?.$id;
  const label = uiSchema?.['ui:title'] || 'Start with a template';
  const description = uiSchema?.['ui:description'] || '';
  const help = uiSchema?.['ui:help'] || '';
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  return (
    <FormControl
      className={classes.root}
      margin="normal"
      required={required}
      error={(rawErrors?.length > 0 && !formData) || !!fetchError}
      fullWidth
    >
      <Box className={classes.labelContainer}>
        <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
          {label}
          {required ? ' *' : ' (Optional)'}
        </InputLabel>
        {docsUrl && (
          <DocsHelpIcon
            docsUrl={docsUrl}
            tooltipText="GitHub template documentation"
          />
        )}
      </Box>
      <Select
        id={id}
        value={formData || ''}
        onChange={handleChange}
        disabled={loading}
      >
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            <span>🔄 Loading templates from GitHub...</span>
          </MenuItem>
        )}
        {!loading && !orgName && (
          <MenuItem disabled>
            <span>⬆️ Please select an organization first</span>
          </MenuItem>
        )}
        {!loading &&
          orgName &&
          templates.map(template => (
            <MenuItem key={template.fullName} value={template.fullName}>
              <div className={classes.templateItem}>
                <div className={classes.templateName}>
                  {template.fullName === '' ? '📄' : '📦'} {template.name}
                  {template.language && (
                    <Chip
                      label={template.language}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className={classes.languageChip}
                    />
                  )}
                </div>
                {/* {template.description && (
                  <div className={classes.templateDescription}>
                    {template.description}
                  </div>
                )} */}
              </div>
            </MenuItem>
          ))}
      </Select>
      {fetchError && <FormHelperText error>{fetchError}</FormHelperText>}
      {description && <FormHelperText>{description}</FormHelperText>}
      {help && <FormHelperText>{help}</FormHelperText>}
      {loading && (
        <FormHelperText style={{ color: '#1976d2' }}>
          🔄 Loading templates from GitHub...
        </FormHelperText>
      )}
    </FormControl>
  );
};

/**
 * Export the field extension properly using scaffolderPlugin.provide
 */
export const GithubTemplatePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GithubTemplatePicker',
    component: GithubTemplatePickerComponent,
  }),
);
